import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Store, Mail, Lock, Loader2, Globe, Check, X, Eye, EyeOff, ArrowLeft, ArrowRight, TrendingUp, Zap, Shield, BarChart3, Gift, Briefcase, User, Users, Wallet, Calendar, Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { PlatformSelector, type PlatformType } from "@/components/PlatformSelector";
import { setBypassBlocker } from "@/hooks/useNavigationBlocker";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  finalizePendingShopifyInstall,
  clearPendingShopifyContext,
  getPendingShopifyContext,
  isPendingShopifyInstallToken,
  setPendingShopifyContext,
} from "@/lib/shopifyInstall";
import {
  buildEmbeddedAppPath,
  getShopifyEmbeddedContext,
  isEmbeddedShopifyLaunch,
  openShopifyTopLevel,
  setShopifyEmbeddedContext,
} from "@/lib/shopifyEmbedded";
import { buildPublicAppUrl } from "@/lib/appConfig";
import {
  registerMerchantAccount,
  prepareShopifyEmbeddedInstall,
  registerShopifyMerchantAccount,
  resendMerchantVerificationEmail,
  verifyMerchantEmail,
} from "@/lib/merchantRegistration";
import {
  completePendingMerchantSetup,
  savePendingMerchantSetup,
  type PendingMerchantSetup,
} from "@/lib/merchantSetup";

type UserType = "business" | "customer";

const SESSION_KEY = "shakatah_customer_session";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function UnifiedLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, signIn, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [shopifySignupLoading, setShopifySignupLoading] = useState(false);
  const [shopifyRequiresLogin, setShopifyRequiresLogin] = useState(false);
  const shopifyAutoSignupStarted = useRef(false);
  const platformFromUrl = searchParams.get("platform");

  // Check if this is a Shopify-specific flow (from app install)
  const isShopifyMode = platformFromUrl === "shopify";
  const pendingContext = getPendingShopifyContext();
  const embeddedShopifyLaunch = isEmbeddedShopifyLaunch(searchParams);
  
  // Check for Shopify install context early (needed for initial user type)
  const hasShopifyInstallContext = 
    isShopifyMode || 
    searchParams.get("shop") ||
    pendingContext.shop ||
    pendingContext.sessionToken;

  const getShopifyRedirectPath = (path: string) => {
    if (!path.startsWith("/")) {
      return path;
    }

    const embeddedContext = getShopifyEmbeddedContext(searchParams);
    if (!hasShopifyInstallContext && !embeddedContext.embedded) {
      return path;
    }

    return buildEmbeddedAppPath(path);
  };

  // Get initial tab from URL or default to business
  const getInitialUserType = (): UserType => {
    // If Shopify install context, always default to business (merchant)
    if (hasShopifyInstallContext) return "business";
    const type = searchParams.get("type");
    if (type === "customer") return type;
    return "business";
  };
  
  const [userType, setUserType] = useState<UserType>(getInitialUserType());

  // Redirect authenticated users away from login page
  // Only redirect if not currently in a loading state (prevents race with login handler)
  useEffect(() => {
    const handleLoggedInRedirect = async () => {
      if (!authLoading && !loading && user) {
        // Check for pending Shopify installation - need to handle this flow even for logged-in users
        const storedContext = getPendingShopifyContext();
        
        if (storedContext.shop && storedContext.sessionToken) {
          // Logged-in user with pending Shopify data - need to link/create merchant
          try {
            await finalizePendingShopifyInstall(storedContext.sessionToken);
            
            toast.success("Shopify store connected successfully!");
            setBypassBlocker(true);
            navigate(getShopifyRedirectPath("/merchant/onboarding"), { replace: true });
            setTimeout(() => setBypassBlocker(false), 100);
            return;
          } catch (err) {
            console.error("Error handling Shopify for logged-in user:", err);
          }
        }
        
        try {
          const merchantId = await completePendingMerchantSetup(user);
          if (merchantId) {
            localStorage.setItem("active_merchant_id", merchantId);
            toast.success("Store setup completed successfully.");
            setBypassBlocker(true);
            navigate(getShopifyRedirectPath("/merchant/onboarding"), { replace: true });
            setTimeout(() => setBypassBlocker(false), 100);
            return;
          }
        } catch (err) {
          console.error("Error completing pending merchant setup:", err);
          toast.error("We could not finish your store setup. Please try signing in again.");
        }

        // Normal redirect for logged-in users without pending setup data
        setBypassBlocker(true);
        const redirectUrl = searchParams.get("redirect") || "/merchant-dashboard";
        navigate(getShopifyRedirectPath(redirectUrl), { replace: true });
        setTimeout(() => setBypassBlocker(false), 100);
      }
    };
    
    handleLoggedInRedirect();
  }, [user, authLoading, loading, navigate, searchParams]);
  
  // Check for Shopify install context (from OAuth callback)
  const pendingShopifyShop = pendingContext.shop || searchParams.get("shop");
  // Shopify's embedded launch also has a `session` query parameter, but that
  // value is not a UUID. Accept only our short-lived UUID install session so
  // the redirect handoff works inside Shopify without mistaking App Bridge's
  // session id for an install session.
  const urlPendingShopifySession = searchParams.get("session");
  const pendingShopifySession = pendingContext.sessionToken || (
    isPendingShopifyInstallToken(urlPendingShopifySession) ? urlPendingShopifySession : null
  );
  const pendingShopName = pendingContext.shopName || searchParams.get("shop_name");
  const pendingShopEmail = pendingContext.shopEmail || searchParams.get("shop_email");
  const pendingShopCurrency = pendingContext.shopCurrency || searchParams.get("shop_currency");
  const redirectUrl = searchParams.get("redirect") || "";
  const pendingShopHost = searchParams.get("host") || getShopifyEmbeddedContext(searchParams).host;
  const isShopifyInstall = pendingShopifyShop || redirectUrl.includes("shop=") || isShopifyMode || !!pendingShopifySession;
  const isShopifyInstallFlow = Boolean(pendingShopifyShop && pendingShopifySession);
  const hasEmbeddedShopifySignals = Boolean(
    pendingShopifyShop && (
      searchParams.get("embedded") === "1" ||
      searchParams.has("host") ||
      searchParams.has("id_token") ||
      getShopifyEmbeddedContext(searchParams).embedded
    ),
  );
  const hasCompletedShopifyInstall = Boolean(
    isShopifyMode &&
    !pendingShopifySession &&
    typeof window !== "undefined" &&
    localStorage.getItem("active_merchant_id"),
  );
  
  // Auto-trigger OAuth for Shopify installs that don't yet have a pending install session.
  // This handles when user installs from Shopify admin and app loads with just ?shop=
  useEffect(() => {
    const shopFromUrl = searchParams.get("shop");
    const hostFromUrl = searchParams.get("host") || getShopifyEmbeddedContext(searchParams).host;
    const isEmbeddedLaunch = isEmbeddedShopifyLaunch(searchParams);
    const hasPendingSession = getPendingShopifyContext().sessionToken || (
      !isEmbeddedLaunch && isPendingShopifyInstallToken(searchParams.get("session"))
    );
    const alreadyTriggeredOAuth = sessionStorage.getItem("oauth_triggered");
    
    // For embedded admin launches, render the app/login UI directly.
    // Redirecting to OAuth here would try to open Shopify auth inside the iframe.
    if (shopFromUrl && isEmbeddedLaunch) {
      sessionStorage.removeItem("oauth_triggered");
      return;
    }

    // Legacy non-embedded flow: if we have shop but no install session yet, trigger OAuth.
    if (shopFromUrl && !hasPendingSession && !alreadyTriggeredOAuth && !user) {
      console.log("Shopify install detected without session, triggering OAuth for:", shopFromUrl);
      sessionStorage.setItem("oauth_triggered", "true");
      
      const oauthUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth-init`);
      oauthUrl.searchParams.set("shop", shopFromUrl);
      if (hostFromUrl) {
        oauthUrl.searchParams.set("host", hostFromUrl);
      }
      openShopifyTopLevel(oauthUrl.toString());
    }
  }, [searchParams, user]);
  
  // Clear OAuth triggered flag when we have the install session
  useEffect(() => {
    if (pendingShopifySession) {
      sessionStorage.removeItem("oauth_triggered");
    }
  }, [pendingShopifySession]);
  
  // Store pending Shopify metadata in sessionStorage for use after login/signup
  useEffect(() => {
    if (pendingShopifySession && (pendingShopifyShop || pendingShopName || pendingShopEmail)) {
      setPendingShopifyContext({
        sessionToken: pendingShopifySession,
        shop: pendingShopifyShop,
        shopEmail: pendingShopEmail,
        shopName: pendingShopName,
        shopCurrency: pendingShopCurrency,
      });
    }
  }, [pendingShopEmail, pendingShopCurrency, pendingShopName, pendingShopifySession, pendingShopifyShop]);

  useEffect(() => {
    setShopifyEmbeddedContext({
      embedded: searchParams.get("embedded") === "1" || Boolean(pendingShopHost),
      host: pendingShopHost,
      shop: pendingShopifyShop,
    });
  }, [pendingShopHost, pendingShopifyShop, searchParams]);

  useEffect(() => {
    const verifyToken = searchParams.get("verifyToken");
    if (!verifyToken) {
      return;
    }

    let cancelled = false;

    const runVerification = async () => {
      setAuthTab("login");
      setMerchantVerificationStatus("pending");
      setMerchantVerificationMessage("Verifying your Splitkart email link...");
      setIsVerifyingMerchantEmail(true);

      try {
        const result = await verifyMerchantEmail(verifyToken);
        if (cancelled) {
          return;
        }

        const verifiedEmail = result.email || "";
        setMerchantVerificationEmail(verifiedEmail);
        setMerchantVerificationStatus("success");
        setMerchantVerificationMessage(
          "Your email is verified. Sign in to continue your Splitkart setup.",
        );
        if (verifiedEmail) {
          setLoginEmail(verifiedEmail);
        }
        toast.success(result.alreadyVerified ? "Email already verified." : "Email verified successfully.");
        navigate(
          getShopifyRedirectPath(
            `/merchant-login?tab=login&verified=1${verifiedEmail ? `&email=${encodeURIComponent(verifiedEmail)}` : ""}`,
          ),
          { replace: true },
        );
      } catch (error: any) {
        if (cancelled) {
          return;
        }

        const message = error?.message || "This verification link is invalid or has expired.";
        setMerchantVerificationStatus("error");
        setMerchantVerificationMessage(message);
        toast.error(message);
      } finally {
        if (!cancelled) {
          setIsVerifyingMerchantEmail(false);
        }
      }
    };

    runVerification();

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  
  // Signup form state - auto-fill email from Shopify if available
  const [signupEmail, setSignupEmail] = useState(pendingShopEmail || "");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [storeName, setStoreName] = useState(pendingShopName || "");
  const [storeDomain, setStoreDomain] = useState(pendingShopifyShop || "");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  
  // Platform selection state (for business only)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(
    pendingShopifyShop || pendingShopifySession ? "shopify" : null
  );
  const [signupStep, setSignupStep] = useState<"platform" | "details">(
    pendingShopifyShop || pendingShopifySession ? "details" : "platform"
  );
  
  // Form validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [authTab, setAuthTab] = useState<"login" | "signup">(() => {
    if (pendingShopifySession) {
      return "signup";
    }
    // Never render the legacy password form for an embedded Shopify launch
    // before the cookieless App Bridge registration session is prepared.
    if (hasEmbeddedShopifySignals && searchParams.get("tab") === "signup") {
      return "login";
    }
    if (searchParams.get("tab") === "signup") {
      return "signup";
    }
    if (hasCompletedShopifyInstall) {
      return "login";
    }
    return "login";
  });
  const [merchantVerificationEmail, setMerchantVerificationEmail] = useState(
    () => searchParams.get("email") || "",
  );
  const [merchantVerificationStatus, setMerchantVerificationStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >(() => {
    if (searchParams.get("verificationEmailSent") === "1") {
      return "pending";
    }
    if (searchParams.get("verified") === "1") {
      return "success";
    }
    return "idle";
  });
  const [merchantVerificationMessage, setMerchantVerificationMessage] = useState(() => {
    if (searchParams.get("verificationEmailSent") === "1") {
      return "Check your inbox and click the Splitkart verification link to finish setting up your account.";
    }
    if (searchParams.get("verified") === "1") {
      return "Your email is verified. You can sign in and continue your store setup.";
    }
    return "";
  });
  const [isVerifyingMerchantEmail, setIsVerifyingMerchantEmail] = useState(false);
  const [isResendingMerchantEmail, setIsResendingMerchantEmail] = useState(false);
  
  // Update form fields and tab when Shopify data is available from URL
  useEffect(() => {
    // Auto-switch to signup tab for Shopify installs
    if (pendingShopifySession) {
      setAuthTab("signup");
      setUserType("business");
      setSelectedPlatform("shopify");
      setSignupStep("details"); // Skip platform selection
    }
    if (pendingShopifyShop && !storeDomain) {
      setStoreDomain(pendingShopifyShop);
    }
    if (pendingShopName && !storeName) {
      setStoreName(pendingShopName);
    }
    if (pendingShopEmail && !signupEmail) {
      setSignupEmail(pendingShopEmail);
    }
    if (!pendingShopifyShop && !pendingShopifySession && platformFromUrl === "woocommerce") {
      setAuthTab("signup");
      setUserType("business");
      setSelectedPlatform("woocommerce");
      setSignupStep("details");
    }
    if (hasCompletedShopifyInstall && !pendingShopifySession && searchParams.get("tab") !== "signup") {
      setAuthTab("login");
    }
  }, [hasCompletedShopifyInstall, pendingShopifySession, pendingShopifyShop, pendingShopName, pendingShopEmail, storeDomain, storeName, signupEmail, platformFromUrl]);

  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    if (!emailFromUrl) {
      return;
    }

    setMerchantVerificationEmail(emailFromUrl);
    if (searchParams.get("verified") === "1") {
      setLoginEmail(emailFromUrl);
    }
  }, [searchParams]);

  // Customer OTP verification state
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerOtp, setCustomerOtp] = useState("");
  const [customerStep, setCustomerStep] = useState<"email" | "otp">("email");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  // Check for existing customer session
  useEffect(() => {
    if (userType === "customer") {
      const storedSession = sessionStorage.getItem(SESSION_KEY);
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession);
          if (session.email) {
            // Already verified, redirect to dashboard
            navigate("/my-purchases", { replace: true });
            return;
          }
        } catch {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    }
  }, [userType, navigate]);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!signupPassword) return { score: 0, label: "", color: "" };
    
    let score = 0;
    if (signupPassword.length >= 6) score += 1;
    if (signupPassword.length >= 8) score += 1;
    if (/[A-Z]/.test(signupPassword)) score += 1;
    if (/[0-9]/.test(signupPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(signupPassword)) score += 1;
    
    if (score <= 1) return { score: 20, label: "Weak", color: "bg-red-500" };
    if (score <= 2) return { score: 40, label: "Fair", color: "bg-orange-500" };
    if (score <= 3) return { score: 60, label: "Good", color: "bg-yellow-500" };
    if (score <= 4) return { score: 80, label: "Strong", color: "bg-green-500" };
    return { score: 100, label: "Excellent", color: "bg-green-600" };
  }, [signupPassword]);

  // Validation helpers
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidShopifyDomain = (domain: string) => /^[a-zA-Z0-9-]+\.myshopify\.com$/.test(domain);
  const isValidStoreUrl = (url: string) => {
    try {
      const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
      return ["http:", "https:"].includes(parsed.protocol) && parsed.hostname.includes(".");
    } catch {
      return false;
    }
  };
  const normalizeStoreUrl = (url: string) => {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    const pathname = parsed.pathname.replace(/\/+$/, "");
    return `${parsed.origin}${pathname}`;
  };
  const passwordsMatch = signupPassword === confirmPassword;

  // Reset form when switching user types (but not for Shopify installs)
  useEffect(() => {
    // Don't reset if this is a Shopify install flow
    const isShopifyInstallFlow = pendingShopifyShop || pendingShopifySession;
    if (!isShopifyInstallFlow && platformFromUrl !== "woocommerce") {
      setSignupStep("platform");
      setSelectedPlatform(null);
    }
    setTouched({});
    // Reset customer OTP state
    setCustomerEmail("");
    setCustomerOtp("");
    setCustomerStep("email");
    setOtpError(null);
  }, [userType, pendingShopifyShop, pendingShopifySession, platformFromUrl]);

  // Customer OTP handlers
  const handleSendCustomerOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!customerEmail.trim() || !isValidEmail(customerEmail)) {
      setOtpError("Please enter a valid email address");
      return;
    }

    setIsSendingOtp(true);
    setOtpError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-gift-verification-otp", {
        body: { 
          email: customerEmail.trim().toLowerCase(),
          method: "email"
        },
      });

      if (fnError) throw fnError;

      toast.success("Verification code sent! Check your email.");
      setCustomerStep("otp");
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      const errorMessage = await getSupabaseFunctionErrorMessage(
        err,
        "Failed to send verification code",
      );
      setOtpError(errorMessage);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyCustomerOtp = async () => {
    if (customerOtp.length !== 6) {
      setOtpError("Please enter the 6-digit code");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("verify-gift-otp", {
        body: { 
          email: customerEmail.trim().toLowerCase(),
          code: customerOtp,
          method: "email"
        },
      });

      if (fnError) throw fnError;

      if (data?.error) {
        setOtpError(data.error);
        setIsVerifyingOtp(false);
        return;
      }

      // Store session
      const session = {
        email: data.email,
        phone: data.phone,
        method: "email",
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      toast.success("Verified! Redirecting to your dashboard...");
      navigate("/my-purchases", { replace: true });
    } catch (err: any) {
      console.error("Error verifying OTP:", err);
      const errorMessage = await getSupabaseFunctionErrorMessage(
        err,
        "Failed to verify code",
      );
      setOtpError(errorMessage);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendMerchantVerification = async () => {
    const normalizedEmail = merchantVerificationEmail.trim().toLowerCase();
    if (!normalizedEmail || !isValidEmail(normalizedEmail)) {
      toast.error("Enter a valid email address to resend the verification link.");
      return;
    }

    setIsResendingMerchantEmail(true);
    try {
      await resendMerchantVerificationEmail(normalizedEmail);
      setMerchantVerificationStatus("pending");
      setMerchantVerificationMessage(
        "A fresh Splitkart verification link is on the way. Please check your inbox.",
      );
      toast.success("Verification email sent.");
    } catch (error: any) {
      const message = error?.message || "Failed to resend verification email.";
      setMerchantVerificationStatus("error");
      setMerchantVerificationMessage(message);
      toast.error(message);
    } finally {
      setIsResendingMerchantEmail(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    const { error } = await signIn(loginEmail, loginPassword);
    
    if (error) {
      setLoading(false);
      const message = error.message || "Failed to sign in.";
      if (message.toLowerCase().includes("email not confirmed")) {
        const normalizedEmail = loginEmail.trim().toLowerCase();
        let verificationMessage =
          "Please verify your email using the Splitkart link we sent before signing in.";

        try {
          await resendMerchantVerificationEmail(normalizedEmail);
          verificationMessage =
            "We sent you a fresh Splitkart verification link. Please check your inbox and verify your email before signing in.";
          toast.success("Verification email sent.");
        } catch (resendError: any) {
          console.error("Failed to resend merchant verification email during login:", resendError);
          verificationMessage =
            resendError?.message ||
            "Please verify your email using the Splitkart link we sent before signing in.";
          toast.error(verificationMessage);
        }

        setMerchantVerificationEmail(normalizedEmail);
        setMerchantVerificationStatus("pending");
        setMerchantVerificationMessage(verificationMessage);
        return;
      }
      toast.error(message);
    } else {
      // Set remember me preference
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
        sessionStorage.setItem("sessionActive", "true");
      }
      
      const { data: currentAuth } = await supabase.auth.getUser();
      const signedInUser = currentAuth.user;

      try {
        const merchantId = await completePendingMerchantSetup(signedInUser);
        if (merchantId) {
          localStorage.setItem("active_merchant_id", merchantId);
          toast.success("Store setup completed successfully.");
          setBypassBlocker(true);
          await new Promise(resolve => setTimeout(resolve, 300));
          navigate(getShopifyRedirectPath("/merchant/onboarding"), { replace: true });
          setTimeout(() => setBypassBlocker(false), 100);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("Error completing pending merchant setup after login:", err);
        toast.error("We could not finish your store setup. Please try again.");
      }

      // Check for pending Shopify connection after login
      const storedContext = getPendingShopifyContext();
      
      if (storedContext.shop && storedContext.sessionToken) {
        try {
          await finalizePendingShopifyInstall(storedContext.sessionToken);
          toast.success("Shopify store connected successfully!");
          
          toast.success("Welcome back! Let's complete your store setup.");
          setBypassBlocker(true);
          await new Promise(resolve => setTimeout(resolve, 300));
          navigate(getShopifyRedirectPath("/merchant/onboarding?step=quick"), { replace: true });
          setTimeout(() => setBypassBlocker(false), 100);
          setLoading(false);
          return; // Exit early - we've handled the Shopify flow
        } catch (err) {
          console.error("Error linking Shopify store:", err);
          // Don't block login, just log the error
        }
      }
      
      toast.success("Welcome back!");
      // Bypass navigation blocker for redirect
      setBypassBlocker(true);
      
      // Small delay to ensure auth state is fully propagated before navigation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const redirectPath = searchParams.get("redirect") || "/merchant-dashboard";
      navigate(getShopifyRedirectPath(redirectPath), { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      // Store user type preference for post-OAuth handling (both storage for reliability)
      sessionStorage.setItem("oauth_user_type", userType);
      localStorage.setItem("oauth_user_type", userType);
      
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error(error.message || "Failed to sign in with Google");
        setGoogleLoading(false);
      }
      // If successful, user will be redirected to Google
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      toast.error("Failed to initiate Google sign-in");
      setGoogleLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setTouched({ email: true, password: true, confirm: true, store: true, domain: true });
    
    const isShopifyPlatform = selectedPlatform === "shopify";
    const isWooCommercePlatform = selectedPlatform === "woocommerce";
    const shopifyContext = getPendingShopifyContext();
    const pendingShop = shopifyContext.shop;
    const pendingSession = shopifyContext.sessionToken;
    const pendingName = shopifyContext.shopName;
    const isShopifyInstallFlow = Boolean(pendingShop && pendingSession);

    if (isShopifyInstallFlow && pendingSession) {
      await completeShopifyRegistration(pendingSession);
      return;
    }
    
    if (!signupEmail || (!isShopifyInstallFlow && (!signupPassword || !confirmPassword || !storeName))) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (userType === "business" && (isShopifyPlatform || isWooCommercePlatform) && !storeDomain) {
      toast.error(isShopifyPlatform ? "Please enter your Shopify store domain" : "Please enter your WooCommerce store URL");
      return;
    }
    
    if (!isValidEmail(signupEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (!isShopifyInstallFlow && !passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (!isShopifyInstallFlow && signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (userType === "business" && isShopifyPlatform && !isValidShopifyDomain(storeDomain)) {
      toast.error("Please enter a valid Shopify store domain (e.g., your-store.myshopify.com)");
      return;
    }

    if (userType === "business" && isWooCommercePlatform && !isValidStoreUrl(storeDomain)) {
      toast.error("Please enter a valid WooCommerce store URL (e.g., https://yourstore.com)");
      return;
    }
    
    setLoading(true);
    
    try {
      if (userType === "business" && (isShopifyPlatform || isWooCommercePlatform) && !isShopifyInstallFlow) {
        const normalizedDomain = isWooCommercePlatform ? normalizeStoreUrl(storeDomain) : storeDomain;
        const { data: existingMerchant } = await supabase
          .from('merchants')
          .select('id')
          .eq('shopify_store_domain', normalizedDomain)
          .maybeSingle();

        if (existingMerchant && !isWooCommercePlatform) {
          toast.error("This store is already registered. Please login or contact support.");
          setLoading(false);
          return;
        }

        if (existingMerchant && isWooCommercePlatform) {
          toast.info("This WooCommerce store is already connected. We'll reconnect it to your new account and refresh the connector credentials after signup.");
        }
      }

      let platformType: string;
      let domainToUse: string;
      let storeNameToUse: string;
      let accessTokenToUse: string | null = null;
      
      if (isShopifyInstallFlow) {
        // Use pending Shopify data from OAuth
        platformType = "shopify";
        domainToUse = pendingShop;
        storeNameToUse = pendingName || pendingShop.replace(".myshopify.com", "");
        console.log("Using pending Shopify data for signup:", pendingShop);
      } else if (selectedPlatform === "shopify") {
        platformType = "shopify";
        domainToUse = storeDomain;
        storeNameToUse = storeName || signupEmail.split('@')[0];
      } else if (selectedPlatform === "woocommerce") {
        platformType = "woocommerce";
        domainToUse = normalizeStoreUrl(storeDomain);
        storeNameToUse = storeName || signupEmail.split('@')[0];
      } else {
        platformType = selectedPlatform || "standalone";
        const nameForDomain = storeName || signupEmail.split('@')[0];
        domainToUse = `${nameForDomain.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}.business.shakatah.com`;
        storeNameToUse = storeName || signupEmail.split('@')[0];
      }

      const pendingMerchantSetup: PendingMerchantSetup = {
        platformType,
        storeDomain: domainToUse,
        storeName: storeNameToUse,
        accessToken: accessTokenToUse,
        widgetInstalled: Boolean(accessTokenToUse),
        billingProvider: platformType === "shopify" ? "shopify" : "razorpay",
        signupSource: platformType === "shopify" && isShopifyInstallFlow ? "shopify_app_store" : "direct_website",
      };

      // Shopify installs are finalized server-side immediately after the
      // account session is created. Keeping this local fallback would cause
      // a duplicate merchant to be created on a later login.
      if (!isShopifyInstallFlow) {
        savePendingMerchantSetup(pendingMerchantSetup);
      }

      let authDataUserId: string | null = null;

      if (userType === "business") {
        if (isShopifyInstallFlow) {
          await completeShopifyRegistration(pendingSession);
          return;
        }

        const registerResult = await registerMerchantAccount({
          email: signupEmail,
          merchantSetup: pendingMerchantSetup,
          password: signupPassword,
        });
        authDataUserId = registerResult.userId || null;
        const normalizedEmail = signupEmail.trim().toLowerCase();
        setMerchantVerificationEmail(normalizedEmail);
        setMerchantVerificationStatus("pending");
        setMerchantVerificationMessage(
          "Check your inbox and click the Splitkart verification link to finish setting up your account.",
        );
        setAuthTab("login");
        toast.success("Verification email sent. Please confirm your email to continue.");
        setLoading(false);
        navigate(
          getShopifyRedirectPath(
            `/merchant-login?tab=login&verificationEmailSent=1&email=${encodeURIComponent(normalizedEmail)}`,
          ),
          { replace: true },
        );
        return;
      } else {
        const { error: signUpError, data: authData } = await supabase.auth.signUp({
          email: signupEmail,
          password: signupPassword,
          options: {
            emailRedirectTo: buildPublicAppUrl("/merchant-login"),
            data: {
              account_type: userType,
              merchant_setup: pendingMerchantSetup,
            },
          }
        });
        
        if (signUpError) {
          toast.error(signUpError.message);
          setLoading(false);
          return;
        }

        if (!authData.user) {
          toast.error("Failed to create account - please try again");
          setLoading(false);
          return;
        }

        authDataUserId = authData.user.id;
      }

      // No need to wait for session - using SECURITY DEFINER RPC function

      if (isShopifyInstallFlow) {
        try {
          await finalizePendingShopifyInstall(pendingSession);
        } catch (shopifyInstallError) {
          console.error("Error finalizing Shopify install after signup:", shopifyInstallError);
          toast.error("Account created but Shopify connection could not be finalized. Please sign in and try again.");
          navigate(getShopifyRedirectPath("/merchant-login"), { replace: true });
          setLoading(false);
          return;
        }
      } else {
        if (userType !== "business") {
          const { error: merchantError } = await supabase
            .rpc('create_merchant_with_user', {
              p_user_id: authDataUserId,
              p_store_domain: domainToUse,
              p_store_name: storeNameToUse,
              p_platform_type: platformType,
              p_access_token: accessTokenToUse,
              p_widget_installed: !!accessTokenToUse,
              p_billing_provider: platformType === "shopify" ? "shopify" : "razorpay",
              p_signup_source: platformType === "shopify" ? "shopify_app_store" : "direct_website",
            });

          if (merchantError) {
            console.error("Error creating merchant:", merchantError);
            toast.error("Account created but setup failed. Please login to complete setup.");
            navigate(getShopifyRedirectPath("/merchant-login"), { replace: true });
            setLoading(false);
            return;
          }
        }

        localStorage.removeItem("splitkart_pending_merchant_setup");
      }

      // Set session persistence flags (same as login)
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
        sessionStorage.setItem("sessionActive", "true");
      }

      // Clear pending Shopify data if it was used
      toast.success("Account created! Let's set up your profile.");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      
      // Redirect to appropriate onboarding based on actual platform type (not just user type selection)
      // If Shopify install, always go to merchant onboarding
      let defaultRedirect: string;
      if (isShopifyInstallFlow || platformType === "shopify") {
        defaultRedirect = isShopifyInstallFlow ? "/merchant/onboarding?step=quick" : "/merchant/onboarding";
      } else {
        defaultRedirect = "/merchant/onboarding";
      }
      
      const redirectUrl = searchParams.get("redirect") || defaultRedirect;
      navigate(getShopifyRedirectPath(redirectUrl), { replace: true });
      // Reset bypass flag after navigation
      setTimeout(() => setBypassBlocker(false), 100);
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const completeShopifyRegistration = async (sessionToken: string) => {
    setShopifySignupLoading(true);
    setLoading(true);
    let shopifyRegistrationStage = "register";

    try {
      const shopifyRegistration = await registerShopifyMerchantAccount(
        sessionToken,
        searchParams.get("id_token"),
      );
      shopifyRegistrationStage = "verify-session";
      const { error: sessionError } = await supabase.auth.verifyOtp({
        token_hash: shopifyRegistration.tokenHash,
        type: "email",
      });

      if (sessionError) {
        throw sessionError;
      }

      shopifyRegistrationStage = "finalize-install";
      const finalizedInstall = await finalizePendingShopifyInstall(sessionToken, {
        completeOnboarding: true,
      });

      if (!finalizedInstall?.merchantId) {
        throw new Error("Shopify store connection could not be finalized");
      }

      localStorage.setItem("active_merchant_id", String(finalizedInstall.merchantId));
      localStorage.setItem("rememberMe", "true");
      clearPendingShopifyContext();

      toast.success("Shopify connected. Opening your dashboard...");
      setBypassBlocker(true);
      navigate(getShopifyRedirectPath("/merchant-dashboard"), { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
    } catch (error: any) {
      console.error(`Failed to complete embedded Shopify registration (${shopifyRegistrationStage}):`, error);
      const status = Number(error?.status);
      if (status === 404 || status === 410) {
        // A previous interrupted test can leave the short-lived install
        // session in storage. Drop it and prepare a fresh session once.
        clearPendingShopifyContext();
        setShopifySignupLoading(false);
        setLoading(false);
        setTimeout(() => {
          void startShopifySignup(true);
        }, 0);
        return;
      }
      toast.error(error?.message || "Could not open your Shopify dashboard. Please try again.");
      setShopifySignupLoading(false);
      setLoading(false);
    }
  };

  const startShopifySignup = async (force = false) => {
    if (!isShopifyInstall || !hasEmbeddedShopifySignals || (shopifySignupLoading && !force)) {
      return false;
    }

    setShopifySignupLoading(true);
    try {
      const result = await prepareShopifyEmbeddedInstall(searchParams.get("id_token"));

      if (result.requiresLogin) {
        setShopifyRequiresLogin(true);
        setLoginEmail(result.email || "");
        setAuthTab("login");
        setShopifySignupLoading(false);
        toast.info("This Shopify store is already connected. Please log in to continue.");
        return true;
      }

      if (!result.sessionToken) {
        throw new Error("Shopify registration session was not created");
      }

      setPendingShopifyContext({
        sessionToken: result.sessionToken,
        shop: result.shop,
        shopEmail: result.email,
        shopName: result.shopName,
        shopCurrency: result.shopCurrency,
      });
      await completeShopifyRegistration(result.sessionToken);
    } catch (error: any) {
      console.error("Failed to prepare embedded Shopify signup:", error);
      toast.error(error?.message || "Could not connect your Shopify store. Please try again.");
      setShopifySignupLoading(false);
    }
    return true;
  };

  // Embedded Shopify launches are already authenticated by Shopify. Complete
  // first-time registration in the background and go straight to the app;
  // existing stores are returned to the normal login form by the server.
  useEffect(() => {
    if (
      user ||
      authLoading ||
      !isShopifyInstall ||
      !hasEmbeddedShopifySignals ||
      shopifyAutoSignupStarted.current
    ) {
      return;
    }

    shopifyAutoSignupStarted.current = true;
    if (pendingShopifySession) {
      void completeShopifyRegistration(pendingShopifySession);
    } else {
      void startShopifySignup();
    }
  }, [authLoading, hasEmbeddedShopifySignals, isShopifyInstall, pendingShopifySession, user]);

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Benefits per user type
  const benefits = {
    business: [
      { icon: <TrendingUp className="h-5 w-5" />, title: "Boost Average Order Value", description: "40% higher AOV with group purchases for birthdays & weddings" },
      { icon: <Zap className="h-5 w-5" />, title: "5-Minute Setup", description: "Install widget, configure settings, start accepting group payments" },
      { icon: <Shield className="h-5 w-5" />, title: "Inventory Protection", description: "Automatic reservation when a group purchase is initiated" },
      { icon: <BarChart3 className="h-5 w-5" />, title: "Real-time Dashboard", description: "Track orders, payments, and revenue in one place" },
    ],
    customer: [
      { icon: <Heart className="h-5 w-5" />, title: "Track Your Gifts", description: "See all group gifts you've organized or contributed to" },
      { icon: <Users className="h-5 w-5" />, title: "Coordinate with Friends", description: "Split costs easily with group discussion features" },
      { icon: <Gift className="h-5 w-5" />, title: "Special Occasions", description: "Never miss a birthday, wedding, or celebration" },
      { icon: <Shield className="h-5 w-5" />, title: "Secure Payments", description: "Your contributions are protected and tracked" },
    ],
  };

  const userTypeConfig = {
    business: {
      icon: <Store className="h-5 w-5" />,
      title: "Business Portal",
      subtitle: "For stores & e-commerce",
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    customer: {
      icon: <User className="h-5 w-5" />,
      title: "Customer Portal",
      subtitle: "Track your purchases & gifts",
      color: "text-pink-600",
      bgColor: "bg-pink-100 dark:bg-pink-950/30",
    },
  };

  const currentConfig = userTypeConfig[userType];

  const handleAuthTabChange = (value: string) => {
    const nextTab = value as "login" | "signup";

    // Keep the manual tab as a retry path if automatic Shopify setup failed.
    if (nextTab === "signup" && !pendingShopifySession && hasEmbeddedShopifySignals) {
      void startShopifySignup();
      return;
    }

    setAuthTab(nextTab);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg text-foreground">
            <Gift className="w-5 h-5 text-primary" />
            Shakatah
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Support
            </Link>
            <a href={buildPublicAppUrl("/my-purchases")} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Track Purchase
            </a>
          </nav>
        </div>
      </header>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-4 md:py-6">
        {/* User Type Selector - Top Pills (hidden in Shopify mode) */}
        {!isShopifyMode && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-1 p-1 bg-muted/50 rounded-full border border-border">
              <button
                onClick={() => setUserType("business")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  userType === "business" 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Store className="h-4 w-4" />
                Business
              </button>
              <button
                onClick={() => setUserType("customer")}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  userType === "customer" 
                    ? "bg-pink-600 text-white shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <User className="h-4 w-4" />
                Customer
              </button>
            </div>
          </div>
        )}
        
        {/* Shopify Mode Header with Made for Shopify badge */}
        {isShopifyMode && (
          <div className="flex flex-col items-center gap-3 mb-6">
            {/* Made for Shopify Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#95BF47] to-[#5E8E3E] rounded-full shadow-sm">
              <svg className="h-4 w-4 text-white" viewBox="0 0 109.5 124.5" fill="currentColor">
                <path d="M74.7,14.8c-0.1-0.1-1.8-0.4-1.8-0.4s-1.2-1.2-1.3-1.3c0,0,0,0-0.1,0c0,0-0.1,0-0.1,0l-3,0.9 c-0.4-1.1-0.9-2.4-1.6-3.8c-2.4-4.6-5.8-7.1-10-7.1c0,0,0,0,0,0c-0.3,0-0.6,0-0.9,0.1c-0.1-0.2-0.3-0.3-0.4-0.5 c-1.8-1.9-4.2-2.9-6.9-2.8c-5.4,0.2-10.7,4-15,10.8c-3,4.8-5.3,10.8-6,15.5c-6.1,1.9-10.4,3.2-10.5,3.3c-3.1,1-3.2,1.1-3.6,4 C13.2,35.3,0,140.6,0,140.6l75.4,13l32.4-8.1C107.8,145.4,74.8,14.9,74.7,14.8z M57.2,20l-4.3,1.3c0-0.2,0-0.5,0-0.7 c0-3.4-0.5-6.1-1.2-8.2C54.9,13,56.5,16.2,57.2,20z M47.5,13c0.8,2,1.3,4.9,1.3,8.8c0,0.3,0,0.6,0,0.8l-8.8,2.7 c1.7-6.5,4.9-9.6,7.5-12.3C47.5,13,47.5,13,47.5,13z M43.5,4.2c0.6,0,1.2,0.2,1.7,0.5c-3.6,1.7-7.4,5.9-9,14.4l-6.9,2.1 C31.6,13.3,37.3,4.4,43.5,4.2z"/>
              </svg>
              <span className="text-xs font-semibold text-white tracking-wide">Made for Shopify</span>
            </div>
            
            {/* Store Login Label */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#95BF47]/10 rounded-full border border-[#95BF47]/20">
              <svg className="h-4 w-4 text-[#95BF47]" viewBox="0 0 109.5 124.5" fill="currentColor">
                <path d="M74.7,14.8c-0.1-0.1-1.8-0.4-1.8-0.4s-1.2-1.2-1.3-1.3c0,0,0,0-0.1,0c0,0-0.1,0-0.1,0l-3,0.9 c-0.4-1.1-0.9-2.4-1.6-3.8c-2.4-4.6-5.8-7.1-10-7.1c0,0,0,0,0,0c-0.3,0-0.6,0-0.9,0.1c-0.1-0.2-0.3-0.3-0.4-0.5 c-1.8-1.9-4.2-2.9-6.9-2.8c-5.4,0.2-10.7,4-15,10.8c-3,4.8-5.3,10.8-6,15.5c-6.1,1.9-10.4,3.2-10.5,3.3c-3.1,1-3.2,1.1-3.6,4 C13.2,35.3,0,140.6,0,140.6l75.4,13l32.4-8.1C107.8,145.4,74.8,14.9,74.7,14.8z M57.2,20l-4.3,1.3c0-0.2,0-0.5,0-0.7 c0-3.4-0.5-6.1-1.2-8.2C54.9,13,56.5,16.2,57.2,20z M47.5,13c0.8,2,1.3,4.9,1.3,8.8c0,0.3,0,0.6,0,0.8l-8.8,2.7 c1.7-6.5,4.9-9.6,7.5-12.3C47.5,13,47.5,13,47.5,13z M43.5,4.2c0.6,0,1.2,0.2,1.7,0.5c-3.6,1.7-7.4,5.9-9,14.4l-6.9,2.1 C31.6,13.3,37.3,4.4,43.5,4.2z"/>
              </svg>
              <span className="text-sm font-medium text-foreground">Shopify Store Login</span>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start max-w-6xl mx-auto">
          {/* Left side - Benefits */}
          <div className="hidden lg:block">
            <div className="mb-4">
              {isShopifyMode ? (
                <>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#95BF47]/10 text-[#95BF47] mb-3">
                    <Store className="h-6 w-6" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">
                    Complete Your Shopify Setup
                  </h1>
                  <p className="text-base text-muted-foreground">
                    Sign in to connect your Shopify store and enable group purchases
                  </p>
                </>
              ) : (
                <>
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${currentConfig.bgColor} ${currentConfig.color} mb-3`}>
                    {currentConfig.icon}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">
                    {userType === "business" && "Grow with Group Purchases"}
                    {userType === "customer" && "Track Your Contributions"}
                  </h1>
                  <p className="text-base text-muted-foreground">
                    {userType === "business" && "Enable customers to split payments and gift together on your store"}
                    {userType === "customer" && "View and manage all your group purchases and gift contributions"}
                  </p>
                </>
              )}
            </div>

            {isShopifyMode ? (
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-2.5 py-0.5 rounded-full bg-[#95BF47]/10 text-[#95BF47] text-xs font-medium">Shopify App</span>
              </div>
            ) : hasEmbeddedShopifySignals && !shopifyRequiresLogin ? (
              <CardContent className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
                {shopifySignupLoading ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="font-medium">Connecting your Shopify store...</p>
                    <p className="text-sm text-muted-foreground">Opening your dashboard automatically.</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">We could not open your Shopify dashboard.</p>
                    <p className="text-sm text-muted-foreground">Please try again.</p>
                    <Button
                      type="button"
                      onClick={() => {
                        shopifyAutoSignupStarted.current = false;
                        void startShopifySignup(true);
                      }}
                    >
                      Try again
                    </Button>
                  </>
                )}
              </CardContent>
            ) : (
              <>
                {userType === "business" && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">Shopify</span>
                    <span className="px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">WooCommerce</span>
                    <span className="px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">API</span>
                  </div>
                )}

              </>
            )}

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-sm">
                {isShopifyMode ? "Why Shopify merchants love Shakatah:" : (
                  <>
                    {userType === "business" && "Why businesses love Shakatah:"}
                    {userType === "customer" && "What you can do:"}
                  </>
                )}
              </h3>
              {benefits[userType].map((benefit, index) => (
                <div key={index} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-card border border-border">
                  <div className={`p-1.5 rounded-md ${isShopifyMode ? "bg-[#95BF47]/10 text-[#95BF47]" : `${currentConfig.bgColor} ${currentConfig.color}`}`}>
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">{benefit.title}</h4>
                    <p className="text-xs text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Login Form */}
          <Card className="w-full max-w-md mx-auto lg:mx-0">
            <CardHeader className="text-center pb-2 pt-4">
              <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 ${isShopifyMode ? "bg-[#95BF47]/10" : currentConfig.bgColor}`}>
                <span className={isShopifyMode ? "text-[#95BF47]" : currentConfig.color}>
                  {isShopifyMode ? <Store className="h-5 w-5" /> : currentConfig.icon}
                </span>
              </div>
              <CardTitle className="text-xl">
                {isShopifyMode ? "Shopify Store Login" : (isShopifyInstall ? "Complete Shopify Setup" : currentConfig.title)}
              </CardTitle>
              <CardDescription className="text-sm">
                {isShopifyMode 
                  ? "Sign in to access your Shakatah dashboard"
                  : (isShopifyInstall 
                    ? `Sign in or create an account to connect ${pendingShopifyShop || "your store"}`
                    : currentConfig.subtitle)
                }
              </CardDescription>
            </CardHeader>
            
            {userType === "customer" ? (
              // Customer portal - OTP-based verification
              <CardContent className="space-y-4 pt-2 pb-4">
                {customerStep === "email" ? (
                  <form onSubmit={handleSendCustomerOtp} className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Enter your email to access your gift and group purchase dashboard.
                    </p>
                    
                    <div className="space-y-2">
                      <Label htmlFor="customer-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="customer-email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10"
                          value={customerEmail}
                          onChange={(e) => {
                            setCustomerEmail(e.target.value);
                            setOtpError(null);
                          }}
                          disabled={isSendingOtp}
                          autoComplete="email"
                        />
                      </div>
                      {otpError && (
                        <p className="text-xs text-destructive">{otpError}</p>
                      )}
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-pink-600 hover:bg-pink-700" 
                      disabled={isSendingOtp || !customerEmail.trim()}
                    >
                      {isSendingOtp ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending code...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Verification Code
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      We'll send a 6-digit code to verify your email
                    </p>

                    {/* Google Sign-in Divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">or</span>
                      </div>
                    </div>

                    <Button 
                      type="button"
                      variant="outline" 
                      className="w-full" 
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading || isSendingOtp}
                    >
                      {googleLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      Continue with Google
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Enter the 6-digit code sent to
                      </p>
                      <p className="font-medium text-foreground">{customerEmail}</p>
                    </div>
                    
                    <div className="flex justify-center">
                      <InputOTP 
                        maxLength={6} 
                        value={customerOtp}
                        onChange={(value) => {
                          setCustomerOtp(value);
                          setOtpError(null);
                        }}
                        disabled={isVerifyingOtp}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    
                    {otpError && (
                      <p className="text-xs text-destructive text-center">{otpError}</p>
                    )}

                    <Button 
                      className="w-full bg-pink-600 hover:bg-pink-700" 
                      onClick={handleVerifyCustomerOtp}
                      disabled={isVerifyingOtp || customerOtp.length !== 6}
                    >
                      {isVerifyingOtp ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Verify & Continue
                        </>
                      )}
                    </Button>
                    
                    <div className="flex items-center justify-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCustomerStep("email");
                          setCustomerOtp("");
                          setOtpError(null);
                        }}
                        disabled={isVerifyingOtp}
                      >
                        <ArrowLeft className="mr-1 h-3 w-3" />
                        Change email
                      </Button>
                      <span className="text-muted-foreground">|</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleSendCustomerOtp}
                        disabled={isSendingOtp || isVerifyingOtp}
                      >
                        {isSendingOtp ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : null}
                        Resend code
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            ) : (
              // Business & Professional - standard login/signup
              <Tabs value={authTab} onValueChange={handleAuthTabChange} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mx-6" style={{ width: 'calc(100% - 48px)' }}>
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup" disabled={shopifySignupLoading}>Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin}>
                    <CardContent className="space-y-3 pt-2 pb-3">
                      {merchantVerificationStatus !== "idle" && (
                        <div
                          className={`rounded-lg border px-3 py-3 text-sm ${
                            merchantVerificationStatus === "success"
                              ? "border-green-200 bg-green-50 text-green-800"
                              : merchantVerificationStatus === "error"
                                ? "border-destructive/20 bg-destructive/5 text-destructive"
                                : "border-primary/20 bg-primary/5 text-foreground"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {isVerifyingMerchantEmail || isResendingMerchantEmail ? (
                              <Loader2 className="mt-0.5 h-4 w-4 animate-spin" />
                            ) : merchantVerificationStatus === "success" ? (
                              <Check className="mt-0.5 h-4 w-4" />
                            ) : merchantVerificationStatus === "error" ? (
                              <X className="mt-0.5 h-4 w-4" />
                            ) : (
                              <Mail className="mt-0.5 h-4 w-4" />
                            )}
                            <div className="min-w-0 flex-1 space-y-2">
                              <p>{merchantVerificationMessage}</p>
                              {merchantVerificationEmail && (
                                <p className="text-xs opacity-80">
                                  Verification email: {merchantVerificationEmail}
                                </p>
                              )}
                              {merchantVerificationStatus !== "success" && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleResendMerchantVerification}
                                  disabled={isVerifyingMerchantEmail || isResendingMerchantEmail}
                                >
                                  {isResendingMerchantEmail ? (
                                    <>
                                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                      Resending...
                                    </>
                                  ) : (
                                    "Resend verification email"
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="login-email"
                            type="email"
                            placeholder="store@example.com"
                            className="pl-10"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            disabled={loading}
                            autoComplete="email"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="login-password"
                            type={showLoginPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className="pl-10 pr-10"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            disabled={loading}
                            autoComplete="current-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                            tabIndex={-1}
                          >
                            {showLoginPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="remember-me" 
                            checked={rememberMe}
                            onCheckedChange={(checked) => setRememberMe(checked === true)}
                          />
                          <Label 
                            htmlFor="remember-me" 
                            className="text-sm font-normal text-muted-foreground cursor-pointer"
                          >
                            Remember me
                          </Label>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          className="px-0 text-sm text-muted-foreground hover:text-primary"
                          onClick={async () => {
                            if (!loginEmail) {
                              toast.error("Please enter your email address first");
                              return;
                            }
                            if (!isValidEmail(loginEmail)) {
                              toast.error("Please enter a valid email address");
                              return;
                            }
                            setLoading(true);
                            try {
                              // Use custom edge function for branded password reset email
                              const response = await supabase.functions.invoke('send-password-reset', {
                                body: { 
                                  email: loginEmail,
                                  redirectTo: buildPublicAppUrl("/reset-password"),
                                }
                              });
                              
                              if (response.error) {
                                throw response.error;
                              }
                              
                              toast.success("Password reset email sent! Check your inbox.");
                            } catch (error: any) {
                              console.error("Password reset error:", error);
                              toast.error("Failed to send reset email. Please try again.");
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                        >
                          Forgot password?
                        </Button>
                      </div>
                    </CardContent>
                    <CardFooter className="pt-2 pb-4">
                      <Button 
                        type="submit" 
                        className={`w-full ${isShopifyMode ? "bg-[#95BF47] hover:bg-[#7BA33D] text-white" : ""}`}
                        size="sm" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          "Sign In"
                        )}
                      </Button>

                    </CardFooter>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  {userType === "business" && signupStep === "platform" ? (
                    <div>
                      <CardContent className="space-y-4">
                        <div className="text-center mb-2">
                          <p className="text-sm text-muted-foreground">
                            Choose how you want to integrate Shakatah
                          </p>
                        </div>
                        <PlatformSelector
                          selectedPlatform={selectedPlatform}
                          onSelect={setSelectedPlatform}
                          disabled={loading}
                        />
                      </CardContent>
                      <CardFooter>
                        <Button 
                          type="button" 
                          className="w-full" 
                          disabled={!selectedPlatform}
                          onClick={() => setSignupStep("details")}
                        >
                          Continue
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardFooter>
                    </div>
                  ) : (
                    <form onSubmit={handleSignup}>
                      <CardContent className="space-y-4">
                        {userType === "business" && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSignupStep("platform")}
                            className="p-0 h-auto text-muted-foreground hover:text-foreground"
                          >
                            <ArrowLeft className="mr-1 h-3 w-3" />
                            Back to platform selection
                          </Button>
                        )}
                        
                        {/* Type badge */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg ${currentConfig.bgColor}`}>
                          <span className={currentConfig.color}>{currentConfig.icon}</span>
                          <span className="text-sm font-medium">
                            {selectedPlatform === "shopify" ? "Shopify Store" : selectedPlatform === "woocommerce" ? "WooCommerce Store" : selectedPlatform === "standalone" ? "Standalone Store" : "API Integration"}
                          </span>
                        </div>
                        
                        {isShopifyInstallFlow && (
                          <div className="rounded-lg border border-[#95BF47]/30 bg-[#95BF47]/10 p-3">
                            <div className="flex items-start gap-3">
                              <Store className="mt-0.5 h-5 w-5 shrink-0 text-[#5f8f2d]" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium">Shopify store connected</p>
                                <p className="truncate text-sm text-muted-foreground">
                                  {pendingShopName || storeName || pendingShopifyShop}
                                </p>
                                <p className="text-xs text-[#5f8f2d]">
                                  {pendingShopifyShop} · Details imported from Shopify
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {!isShopifyInstallFlow && <div className="space-y-2">
                          <Label htmlFor="store-name">Store Name</Label>
                          <div className="relative">
                            <Store className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="store-name"
                              type="text"
                              placeholder="My Awesome Store"
                              className="pl-10"
                              value={storeName}
                              onChange={(e) => setStoreName(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, store: true }))}
                              disabled={loading}
                            />
                          </div>
                        </div>}
                        
                        {userType === "business" && selectedPlatform === "shopify" && !isShopifyInstallFlow && (
                          <div className="space-y-2">
                            <Label htmlFor="store-domain">Shopify Store Domain</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="store-domain"
                                type="text"
                                placeholder="your-store.myshopify.com"
                                className={`pl-10 pr-10 ${touched.domain && storeDomain && !isValidShopifyDomain(storeDomain) ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                value={storeDomain}
                                onChange={(e) => setStoreDomain(e.target.value.toLowerCase())}
                                onBlur={() => setTouched(prev => ({ ...prev, domain: true }))}
                                disabled={loading || !!pendingShopifyShop}
                              />
                              {touched.domain && storeDomain && (
                                <div className="absolute right-3 top-3">
                                  {isValidShopifyDomain(storeDomain) ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <X className="h-4 w-4 text-destructive" />
                                  )}
                                </div>
                              )}
                            </div>
                            {pendingShopifyShop ? (
                              <p className="text-xs text-green-600">
                                ✓ Auto-filled from Shopify app install
                              </p>
                            ) : touched.domain && storeDomain && !isValidShopifyDomain(storeDomain) ? (
                              <p className="text-xs text-destructive">
                                Must be in format: your-store.myshopify.com
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                Enter your Shopify permanent domain
                              </p>
                            )}
                          </div>
                        )}

                        {userType === "business" && selectedPlatform === "woocommerce" && (
                          <div className="space-y-2">
                            <Label htmlFor="store-domain">WooCommerce Store URL</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="store-domain"
                                type="text"
                                placeholder="https://yourstore.com"
                                className={`pl-10 pr-10 ${touched.domain && storeDomain && !isValidStoreUrl(storeDomain) ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                value={storeDomain}
                                onChange={(e) => setStoreDomain(e.target.value.trim())}
                                onBlur={() => setTouched(prev => ({ ...prev, domain: true }))}
                                disabled={loading}
                              />
                              {touched.domain && storeDomain && (
                                <div className="absolute right-3 top-3">
                                  {isValidStoreUrl(storeDomain) ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <X className="h-4 w-4 text-destructive" />
                                  )}
                                </div>
                              )}
                            </div>
                            {touched.domain && storeDomain && !isValidStoreUrl(storeDomain) ? (
                              <p className="text-xs text-destructive">
                                Enter your full WooCommerce site URL, for example https://yourstore.com
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                This must match the WordPress site where the Splitkart WooCommerce plugin is installed.
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-email"
                              type="email"
                              placeholder="store@example.com"
                              className={`pl-10 pr-10 ${touched.email && signupEmail && !isValidEmail(signupEmail) ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                              value={signupEmail}
                              onChange={(e) => setSignupEmail(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                              disabled={loading || isShopifyInstallFlow}
                              autoComplete="email"
                            />
                            {touched.email && signupEmail && (
                              <div className="absolute right-3 top-3">
                                {isValidEmail(signupEmail) ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-destructive" />
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {isShopifyInstallFlow
                              ? "Email imported from your Shopify store. Your account will be created automatically."
                              : "This email will receive notifications for all activities"}
                          </p>
                        </div>

                        {!isShopifyInstallFlow && <>
                        <div className="space-y-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-password"
                              type={showSignupPassword ? "text" : "password"}
                              placeholder="••••••••"
                              className="pl-10 pr-10"
                              value={signupPassword}
                              onChange={(e) => setSignupPassword(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                              disabled={loading}
                              autoComplete="new-password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={() => setShowSignupPassword(!showSignupPassword)}
                              tabIndex={-1}
                            >
                              {showSignupPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                            </Button>
                          </div>
                          {signupPassword && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Progress value={passwordStrength.score} className={`h-1.5 flex-1 ${passwordStrength.color}`} />
                                <span className={`text-xs font-medium ${passwordStrength.score >= 60 ? 'text-green-600' : passwordStrength.score >= 40 ? 'text-yellow-600' : 'text-destructive'}`}>
                                  {passwordStrength.label}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Use 8+ characters with uppercase, numbers, and symbols
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="confirm-password">Confirm Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="confirm-password"
                              type={showSignupPassword ? "text" : "password"}
                              placeholder="••••••••"
                              className={`pl-10 pr-10 ${touched.confirm && confirmPassword && !passwordsMatch ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, confirm: true }))}
                              disabled={loading}
                              autoComplete="new-password"
                            />
                            {touched.confirm && confirmPassword && (
                              <div className="absolute right-3 top-3">
                                {passwordsMatch ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-destructive" />
                                )}
                              </div>
                            )}
                          </div>
                          {touched.confirm && confirmPassword && !passwordsMatch && (
                            <p className="text-xs text-destructive">Passwords do not match</p>
                          )}
                        </div>
                        </>}
                      </CardContent>
                      <CardFooter className="flex flex-col gap-3">
                        <Button 
                          type="submit" 
                          className={`w-full ${isShopifyMode ? "bg-[#95BF47] hover:bg-[#7BA33D] text-white" : ""}`}
                          disabled={loading || googleLoading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating account...
                            </>
                          ) : (
                            <>{isShopifyInstallFlow ? "Create account & open dashboard" : "Create Account"}</>
                          )}
                        </Button>

                      </CardFooter>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default UnifiedLogin;
